import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Service, ServiceFormData } from '../types/service';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getDashboardTitle, shouldShowBusinessDashboard } from '../utils/navigationUtils';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import { X, Save, ArrowLeft, ChevronDown, Search, Info, DollarSign } from 'lucide-react';
import { GROUPED_EMOJI_ICONS } from '../utils/iconConstants';
import { DurationSplitInput } from '../components/DurationSplitInput';

type EditServiceFormData = Omit<ServiceFormData, 'price' | 'duration'> & {
  // Keep as strings so the user can clear the input (""), matching AddServicePage behavior
  price: string;
  duration: string;
};

interface EditServicePageProps {
  mode?: 'edit' | 'duplicate';
}

function getServiceFormStorageKey(serviceId: string, duplicateMode: boolean): string {
  return duplicateMode
    ? `duplicateServiceFormData_${serviceId}`
    : `editServiceFormData_${serviceId}`;
}

function buildFormDataFromService(serviceData: any, duplicateMode: boolean): EditServiceFormData {
  const baseName = serviceData.name || '';
  return {
    name: duplicateMode ? `Copy of ${baseName}` : baseName,
    description: serviceData.description || '',
    price: serviceData.price != null ? String(serviceData.price) : '',
    duration: serviceData.duration_minutes != null ? String(serviceData.duration_minutes) : '60',
    category_id: serviceData.category_id || serviceData.category?.id || '',
    icon: serviceData.icon || '',
    isAddOn: serviceData.is_add_on || serviceData.isAddOn || false,
    serviceLocation: (serviceData.service_location || serviceData.serviceLocation || 'IN_STORE') as 'IN_STORE' | 'ON_SITE',
    quotable: serviceData.quotable === true,
    quoteRequestInstructions:
      serviceData.quoteRequestInstructions || serviceData.quote_request_instructions || '',
  };
}

async function copyProfessionalServiceAssignments(
  businessId: string,
  sourceServiceId: string,
  targetServiceId: string
): Promise<void> {
  const professionals = await apiService.getBusinessProfessionalsDetailed(businessId);
  await Promise.all(
    professionals.map(async (professional: { id: string }) => {
      try {
        const assignedServices = await apiService.getProfessionalServices(businessId, professional.id);
        const hadSourceService = assignedServices.some(
          (assigned: { id?: string; service_id?: string; serviceId?: string }) =>
            String(assigned.id ?? assigned.service_id ?? assigned.serviceId) === String(sourceServiceId)
        );
        if (hadSourceService) {
          await apiService.assignServiceToProfessional(businessId, professional.id, targetServiceId);
        }
      } catch (assignmentError) {
        console.warn(
          `Failed to copy service assignment for professional ${professional.id}:`,
          assignmentError
        );
      }
    })
  );
}

const EditServicePage: React.FC<EditServicePageProps> = ({ mode = 'edit' }) => {
  const isDuplicateMode = mode === 'duplicate';
  const pageTitle = isDuplicateMode ? 'Duplicate Service' : 'Edit Service';
  const pageDescription = isDuplicateMode
    ? 'Review and adjust the duplicated service before saving'
    : 'Update your service information';
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const [business, setBusiness] = useState<any>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [service, setService] = useState<Service | null>(null);
  const [categories, setCategories] = useState<{ id: string; name: string; icon?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<EditServiceFormData>(() => {
    if (!serviceId) {
      return {
        name: '',
        description: '',
        price: '',
        duration: '60',
        category_id: '',
        icon: '',
        isAddOn: false,
        serviceLocation: 'IN_STORE' as 'IN_STORE' | 'ON_SITE',
        quotable: false,
        quoteRequestInstructions: '',
      };
    }

    const savedFormData = localStorage.getItem(getServiceFormStorageKey(serviceId, isDuplicateMode));
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        return {
          name: parsed?.name ?? '',
          description: parsed?.description ?? '',
          price: parsed?.price === 0 ? '0' : (parsed?.price ?? ''),
          duration: parsed?.duration ?? '60',
          category_id: parsed?.category_id ?? '',
          icon: parsed?.icon ?? '',
          isAddOn: parsed?.isAddOn ?? false,
          serviceLocation: parsed?.serviceLocation ?? ('IN_STORE' as 'IN_STORE' | 'ON_SITE'),
          quotable: parsed?.quotable ?? false,
          quoteRequestInstructions: parsed?.quoteRequestInstructions ?? ''
        };
      } catch (e) {
        console.error('Error parsing saved form data:', e);
      }
    }
    return {
      name: '',
      description: '',
      price: '',
      duration: '60',
      category_id: '',
      icon: '',
      isAddOn: false,
      serviceLocation: 'IN_STORE' as 'IN_STORE' | 'ON_SITE',
      quotable: false,
      quoteRequestInstructions: ''
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);
  const [iconSearchTerm, setIconSearchTerm] = useState('');

  // Save form data to localStorage
  const saveFormDataToStorage = (data: EditServiceFormData) => {
    if (!serviceId) return;
    localStorage.setItem(getServiceFormStorageKey(serviceId, isDuplicateMode), JSON.stringify(data));
  };

  // Fetch service data and categories
  useEffect(() => {
    const fetchData = async () => {
      if (!serviceId) {
        setError('Service ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch service data
        const serviceData = await apiService.getService(serviceId);
        setService(serviceData);
        
        // Fetch categories
        const categoriesData = await apiService.getServiceCategories(serviceData.business_id);
        setCategories(categoriesData);
        
        // Set form data - prioritize localStorage data if it exists
        const savedFormData = localStorage.getItem(getServiceFormStorageKey(serviceId, isDuplicateMode));
        let initialFormData: EditServiceFormData;
        
        if (savedFormData) {
          try {
            const parsed = JSON.parse(savedFormData);
            const serviceDefaults = buildFormDataFromService(serviceData, isDuplicateMode);
            initialFormData = {
              name: parsed?.name ?? serviceDefaults.name,
              description: parsed?.description ?? serviceDefaults.description,
              price: parsed?.price === 0 ? '0' : String(parsed?.price ?? serviceDefaults.price),
              duration: String(parsed?.duration ?? serviceDefaults.duration),
              category_id: parsed?.category_id ?? serviceDefaults.category_id,
              icon: parsed?.icon ?? serviceDefaults.icon,
              isAddOn: parsed?.isAddOn ?? serviceDefaults.isAddOn,
              serviceLocation: parsed?.serviceLocation ?? serviceDefaults.serviceLocation,
              quotable: parsed?.quotable ?? serviceDefaults.quotable,
              quoteRequestInstructions:
                parsed?.quoteRequestInstructions ?? serviceDefaults.quoteRequestInstructions,
            };
          } catch (e) {
            console.error('Error parsing saved form data:', e);
            initialFormData = buildFormDataFromService(serviceData, isDuplicateMode);
          }
        } else {
          initialFormData = buildFormDataFromService(serviceData, isDuplicateMode);
        }
        
        setFormData(initialFormData);
        saveFormDataToStorage(initialFormData);
        
        // Load business data to check service_location
        if (currentBusiness?.shortName) {
          try {
            const businessData = await apiService.getBusiness(currentBusiness.shortName);
            setBusiness(businessData.business || businessData);
          } catch (error) {
            console.error('Error loading business:', error);
          }
        }
        
      } catch (err: any) {
        console.error('Error fetching service data:', err);
        setError(err.response?.data?.message || 'Failed to load service data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serviceId, isDuplicateMode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData.quotable) {
      const priceVal = parseFloat(formData.price);
      if (isNaN(priceVal) || priceVal <= 0) {
        newErrors.price = 'Price must be greater than 0';
      }
    }

    if (!formData.quotable) {
      if (!formData.isAddOn) {
        const durationVal = parseInt(formData.duration, 10);
        if (formData.duration === '' || isNaN(durationVal)) {
          newErrors.duration = 'Duration is required';
        } else if (durationVal < 0) {
          newErrors.duration = 'Duration must be 0 or greater';
        }
      } else if (formData.duration !== '') {
        const durationVal = parseInt(formData.duration, 10);
        if (isNaN(durationVal) || durationVal < 0) {
          newErrors.duration = 'Duration must be 0 or greater';
        }
      }
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !serviceId) return;

    setIsSubmitting(true);
    try {
      const parsedPrice = formData.quotable ? null : parseFloat(formData.price);
      const parsedDuration = formData.quotable
        ? 0
        : formData.isAddOn && formData.duration === ''
          ? 0
          : parseInt(formData.duration, 10);

      const serviceLocation = business?.serviceLocation === 'BOTH'
        ? formData.serviceLocation
        : (business?.serviceLocation || service?.serviceLocation || 'IN_STORE') as 'IN_STORE' | 'ON_SITE';

      if (isDuplicateMode) {
        const businessId = service?.businessId ?? (service as any)?.business_id ?? currentBusiness?.id;
        if (!businessId) {
          setError('Business is required to duplicate this service');
          return;
        }

        const newService = await apiService.createService({
          businessId,
          name: formData.name,
          description: formData.description,
          durationMinutes: parsedDuration,
          price: parsedPrice,
          quotable: formData.quotable === true,
          quoteRequestInstructions: String(formData.quoteRequestInstructions ?? ''),
          categoryId: formData.category_id,
          icon: formData.icon || undefined,
          isActive: service?.isActive ?? true,
          isAddOn: formData.isAddOn || false,
          serviceLocation,
        });

        try {
          await copyProfessionalServiceAssignments(businessId, serviceId, newService.id);
        } catch (assignmentError) {
          console.warn('Failed to copy professional service assignments:', assignmentError);
        }

        localStorage.removeItem(getServiceFormStorageKey(serviceId, true));
        toast.success('Service duplicated successfully');
      } else {
        await apiService.updateService(serviceId, {
          name: formData.name,
          description: formData.description,
          durationMinutes: parsedDuration,
          price: parsedPrice,
          quotable: formData.quotable === true,
          quoteRequestInstructions: String(formData.quoteRequestInstructions ?? ''),
          categoryId: formData.category_id,
          icon: formData.icon || undefined,
          isActive: service?.isActive,
          isAddOn: formData.isAddOn || false,
          serviceLocation: serviceLocation
        });
        localStorage.removeItem(getServiceFormStorageKey(serviceId, false));
        toast.success('Service updated successfully');
      }

      const params = new URLSearchParams(location.search);
      params.set('section', 'services');
      navigate(`/dashboard/business?${params.toString()}`);
    } catch (error: any) {
      console.error(isDuplicateMode ? 'Error duplicating service:' : 'Error updating service:', error);
      setError(error.response?.data?.message || (isDuplicateMode ? 'Failed to duplicate service' : 'Failed to update service'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof EditServiceFormData, value: string | boolean) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    saveFormDataToStorage(newFormData);
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumberInputChange = (field: 'price' | 'duration', value: string) => {
    // Allow empty string so user can clear before typing (matches AddServicePage)
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      saveFormDataToStorage(next);
      return next;
    });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.edit-service-category-dropdown-container')) {
        setCategoryDropdownOpen(false);
      }
      if (!target.closest('.edit-service-icon-dropdown-container')) {
        setIsIconDropdownOpen(false);
        setIconSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter icons based on search term
  const getFilteredIconGroups = () => {
    if (!iconSearchTerm.trim()) {
      return GROUPED_EMOJI_ICONS;
    }

    const searchLower = iconSearchTerm.toLowerCase();
    return GROUPED_EMOJI_ICONS
      .map(group => ({
        ...group,
        icons: group.icons.filter(icon => 
          group.title.toLowerCase().includes(searchLower) ||
          icon.includes(searchLower)
        )
      }))
      .filter(group => group.icons.length > 0);
  };

  const handleIconSelect = (icon: string) => {
    handleInputChange('icon', icon);
    setIsIconDropdownOpen(false);
    setIconSearchTerm('');
  };

  // Check if returning from categories management and refresh categories
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromCategories = urlParams.get('fromCategories');
    
    if (fromCategories === 'true') {
      // Refresh categories when returning from categories management
      const refreshCategories = async () => {
        if (service) {
          try {
            const categoriesData = await apiService.getServiceCategories(service.businessId);
            setCategories(categoriesData);
          } catch (err) {
            console.error('Error refreshing categories:', err);
          }
        }
      };
      refreshCategories();
      
      // Remove the parameter from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('fromCategories');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search, service]);

  const handleCancel = () => {
    // Preserve filter params when navigating back
    const params = new URLSearchParams(location.search);
    params.set('section', 'services');
    navigate(`/dashboard/business?${params.toString()}`);
  };

  const buildManageCategoriesUrl = () => {
    const params = new URLSearchParams(location.search);
    params.set('from', isDuplicateMode ? 'duplicate-service' : 'edit-service');
    params.delete('fromCategories');
    if (serviceId) {
      params.set('serviceId', serviceId);
    }

    return `/categories?${params.toString()}`;
  };



  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Normalized form label classes for Add/Edit Service (title case labels, no CSS uppercase)
  const formLabelClass = 'block text-sm font-semibold text-gray-700 mb-2';
  const formLabelClassInline = 'block text-sm font-semibold text-gray-700';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading service...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
          {shouldShowBusinessDashboard(user!) && (
            <BusinessDashboardSidebar
              isSidebarOpen={isSidebarOpen}
              isSidebarCollapsed={isSidebarCollapsed}
              activeSection="services"
              onClose={() => setIsSidebarOpen(false)}
              onToggleCollapse={toggleSidebar}
            />
          )}
          
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
                <div className="w-10"></div> {/* Spacer for centering */}
              </div>
            </div>
            
            <div className="p-4 md:p-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Service Not Found</h2>
                  <p className="text-gray-600 mt-1 mb-6">{error || 'The service you\'re looking for doesn\'t exist.'}</p>
                  <button
                    onClick={handleCancel}
                    className="aacore-btn2-primary"
                  >
                    Back to Services
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />
      
      <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[auto_1fr] min-h-0">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="services"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
          />
        )}
        
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
              <div className="w-10"></div> {/* Spacer for centering */}
            </div>
          </div>
          
          <div className="p-4 md:p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => {
                  const params = new URLSearchParams(location.search);
                  params.set('section', 'services');
                  navigate(`/dashboard/business?${params.toString()}`);
                }}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Services</span>
              </button>
            </div>

            {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">{pageTitle}</h2>
              <p className="text-gray-600 mt-1">{pageDescription}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate(buildManageCategoriesUrl())}
              className="aacore-btn2-secondary w-full sm:w-auto"
            >
              <i className="bi bi-tags"></i>
              Manage Categories
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form - Two columns: Service Details (left), Pricing (right) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left column: Service Details */}
              <div className="space-y-6 lg:border-r lg:border-gray-200 lg:pr-8">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#0B4778]" />
                  Service Details
                </h2>

                <div>
                  <label className={formLabelClass}>
                    Service Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter name of your service"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className={formLabelClass}>
                    Category <span className="text-red-500">*</span>
                  </label>
                  <div className={`relative edit-service-category-dropdown-container ${errors.category_id ? 'border-red-500' : 'border-gray-300'}`}>
                    <button
                      type="button"
                      onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                      className={`w-full px-3 py-2 pr-8 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 flex items-center justify-between ${
                        errors.category_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <span className="text-gray-900">
                        {formData.category_id
                          ? categories.find(c => c.id === formData.category_id)?.name || 'Select a category'
                          : 'Select a category'
                        }
                      </span>
                    </button>
                    {categoryDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {categories.map(category => (
                          <div
                            key={category.id}
                            className="px-3 py-2 text-gray-900 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              handleInputChange('category_id', category.id);
                              setCategoryDropdownOpen(false);
                            }}
                          >
                            {category.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.category_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>
                  )}
                </div>

                <div>
                  <label className={formLabelClass}>
                    Icon (optional)
                    {!formData.icon && (() => {
                      const cat = categories.find(c => c.id === formData.category_id);
                      return cat?.icon ? ` default=${cat.icon}` : null;
                    })()}
                  </label>
                  <div className="relative edit-service-icon-dropdown-container">
                    <button
                      type="button"
                      onClick={() => {
                        setIsIconDropdownOpen(!isIconDropdownOpen);
                        if (!isIconDropdownOpen) setIconSearchTerm('');
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{formData.icon || '🛠️'}</span>
                        <span className="text-gray-700">{formData.icon ? 'Icon Selected' : 'Select Icon'}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isIconDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isIconDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10 shadow-sm">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={iconSearchTerm}
                              onChange={(e) => setIconSearchTerm(e.target.value)}
                              placeholder="Search icons or categories..."
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-gray-50"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="p-3">
                          {getFilteredIconGroups().length > 0 ? (
                            getFilteredIconGroups().map((group, groupIndex) => (
                              <div key={groupIndex} className="mb-4 last:mb-0">
                                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">{group.title}</h4>
                                <div className="grid grid-cols-8 gap-2 mb-3">
                                  {group.icons.map((icon, iconIndex) => (
                                    <button
                                      key={`${groupIndex}-${iconIndex}`}
                                      type="button"
                                      onClick={() => handleIconSelect(icon)}
                                      className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                                      title={`Select ${icon}`}
                                    >
                                      {icon}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <div className="text-4xl mb-2">🔍</div>
                              <p className="text-sm">No icons found matching "{iconSearchTerm}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className={formLabelClass}>
                    Service Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the service in detail..."
                  />
                </div>

                {!formData.quotable && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={formLabelClassInline}>
                        Duration <span className="text-red-500">*</span>
                      </label>
                      {formData.duration !== '' && !Number.isNaN(parseInt(formData.duration, 10)) && (
                        <span className="text-xs text-gray-500">Total: {parseInt(formData.duration, 10)} minutes</span>
                      )}
                    </div>
                    <DurationSplitInput
                      value={formData.duration}
                      onChange={(next) => handleNumberInputChange('duration', next)}
                      allowEmpty={!!formData.isAddOn}
                      required={!formData.isAddOn}
                      error={errors.duration}
                      hideLabel
                      hideTotal
                    />
                    <p className="text-xs text-gray-500 mt-1">Used for scheduling and staff availability.</p>
                    {formData.isAddOn && (
                      <p className="text-xs text-gray-500 mt-1">Duration is optional for service add-ons.</p>
                    )}
                  </div>
                )}

                {/* Service Location - Only show if business supports BOTH */}
                {business?.serviceLocation === 'BOTH' && (
                  <div>
                    <label className={formLabelClass}>
                      Service Location <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Your business supports both in-store and on-site services. Choose one for this service.
                    </p>
                    <div className="flex gap-6">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="serviceLocation"
                          value="IN_STORE"
                          checked={formData.serviceLocation === 'IN_STORE'}
                          onChange={(e) => handleInputChange('serviceLocation', e.target.value as 'IN_STORE' | 'ON_SITE')}
                          className="mt-1 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778] service-location-radio"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">In-store</div>
                          <div className="text-xs text-gray-500">Business' site</div>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="serviceLocation"
                          value="ON_SITE"
                          checked={formData.serviceLocation === 'ON_SITE'}
                          onChange={(e) => handleInputChange('serviceLocation', e.target.value as 'IN_STORE' | 'ON_SITE')}
                          className="mt-1 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778] service-location-radio"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">On-site</div>
                          <div className="text-xs text-gray-500">Customer's site</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Right column: Pricing */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#0B4778]" />
                  <span>Pricing <span className="text-red-500">*</span></span>
                </h2>

                {/* Price is quotable - toggle */}
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!formData.quotable}
                    onClick={() => {
                      const next = !formData.quotable;
                      const updated = {
                        ...formData,
                        quotable: next,
                        price: next ? '' : formData.price,
                        quoteRequestInstructions: next ? (formData.quoteRequestInstructions || '') : '',
                        ...(next ? { isAddOn: false } : {})
                      };
                      setFormData(updated);
                      saveFormDataToStorage(updated);
                      if (errors.price) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.price;
                          return newErrors;
                        });
                      }
                      if (next) {
                        setErrors(prev => {
                          if (!prev.duration) return prev;
                          const newErrors = { ...prev };
                          delete newErrors.duration;
                          return newErrors;
                        });
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                      formData.quotable ? 'bg-[#0B4778]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                        formData.quotable ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Price is quotable</span>
                    <p className="text-xs text-gray-500 mt-0.5">Customers request a quote instead of seeing a fixed price.</p>
                  </div>
                </div>

                {/* This is a service add-on - toggle (disabled when Price is quotable) */}
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!formData.isAddOn}
                    aria-disabled={!!formData.quotable}
                    onClick={() => !formData.quotable && handleInputChange('isAddOn', !formData.isAddOn)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                      formData.quotable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${formData.isAddOn ? 'bg-[#0B4778]' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                        formData.isAddOn ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div>
                    <span className={`text-sm font-medium ${formData.quotable ? 'text-gray-500' : 'text-gray-900'}`}>This is a service add-on</span>
                    <p className="text-xs text-gray-500 mt-0.5">Add-ons can be added to a main service during booking.</p>
                  </div>
                </div>

                {/* Fixed Price - when not quotable */}
                {!formData.quotable && (
                  <div>
                    <label className={formLabelClass}>
                      Fixed Price ($) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => handleNumberInputChange('price', e.target.value)}
                        className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.price ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0.00"
                      />
                    </div>
                    {errors.price && (
                      <p className="text-red-500 text-sm mt-1">{errors.price}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Set the price customers will pay at booking.</p>
                  </div>
                )}

                {/* Quote Request Instructions - when quotable */}
                {formData.quotable && (
                  <div>
                    <label className={formLabelClass}>
                      Quote Request Instructions (optional)
                    </label>
                    <textarea
                      value={formData.quoteRequestInstructions}
                      onChange={(e) => handleInputChange('quoteRequestInstructions', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Example: Please describe the job, include photos if possible..."
                    />
                    <p className="text-xs text-gray-500 mt-1">These instructions will be shown when the customer requests a quote.</p>
                  </div>
                )}

                {formData.quotable && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50">
                    <Info className="flex-shrink-0 w-5 h-5 text-amber-600" />
                    <p className="text-sm text-gray-700 leading-snug pt-0.5">
                      Since this service is <strong>quotable</strong>, customers submit details and you respond with an offer. If a deposit is required, you can set the required deposit amount when you provide your offer.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-row gap-3 pt-4 lg:justify-end">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="aacore-btn2-outline bg-white border border-gray-300 hover:bg-gray-50 flex-1 min-w-0 lg:flex-initial"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed flex-1 min-w-0 lg:flex-initial"
                  >
                    <Save className="w-4 h-4" />
                    <span>{isSubmitting ? (isDuplicateMode ? 'Saving Service...' : 'Updating Service...') : 'Save Service'}</span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default EditServicePage; 